import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { type SessionFile } from "@/shared/domain/sessions/SessionFile";
import { type PaginatedResponse } from "@/shared/http";

import { sessionQueryKeys } from "./sessionQueryKeys";

export interface UseSessionFilesParams {
  sessionId: string;
  pageToken?: string;
}

/**
 * Fetches one page of file contributions for a Session.
 *
 * Each row is a wire-format `SessionFile` — just `file_id` and an
 * optional time-range pair. Joining against the file system for richer
 * columns (name, size, modified) lives in a follow-up.
 */
export function useSessionFiles({
  sessionId,
  pageToken,
}: UseSessionFilesParams): UseQueryResult<
  PaginatedResponse<SessionFile>,
  Error
> {
  const { sessionService } = useDomainServices();
  const orgId = useCurrentOrgId();
  return useQuery<PaginatedResponse<SessionFile>, Error>({
    queryKey: sessionQueryKeys.files(sessionId, pageToken),
    queryFn: ({ signal }) =>
      sessionService.listSessionFiles(sessionId, {
        abortSignal: signal,
        pageToken,
        resourceOwnerId: orgId,
      }),
  });
}
